/**
 * TDBMonthCloseCheck — Live 6-gate validation strip for the current
 * month. Powers month-close approval. CEO + TDB leaders see the
 * "Approve & Freeze" button when all gates are green.
 *
 * Gates (per Dipali · 19-May-2026):
 *   G1: every transaction has a valid active category
 *   G2: reconciliation against MIS Excel
 *   G3: stock register complete + signed by Roji
 *   G4: no unallocated Shopify orders
 *   G5: inter-branch transfers balanced
 *   G6: no negative amounts, no future-dated transactions
 */
import { useEffect, useState, useCallback } from 'react';
import { CheckCircle2, XCircle, Lock, RefreshCw, ShieldCheck } from 'lucide-react';
import api from '@/lib/api';

function thisMonthYM() {
  const ist = new Date(Date.now() + 5.5 * 3600 * 1000);
  return ist.toISOString().slice(0, 7);
}

export default function TDBMonthCloseCheck({ className = '' }) {
  const [monthYM, setMonthYM] = useState(thisMonthYM());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const r = await api.get('/tdb-mis/month-close', { params: { month_ym: monthYM } });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load month-close status');
    } finally {
      setLoading(false);
    }
  }, [monthYM]);

  useEffect(() => { load(); }, [load]);

  const approve = useCallback(async () => {
    if (!window.confirm(`Approve & freeze ${monthYM}? This action is logged in the audit trail.`)) return;
    setApproving(true);
    setErr('');
    try {
      await api.post('/tdb-mis/month-close/approve', { month_ym: monthYM });
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Approval failed');
    } finally {
      setApproving(false);
    }
  }, [monthYM, load]);

  const closed = data?.current_status === 'closed';
  const canClose = !!data?.can_close;
  const gates = data?.gates || [];

  return (
    <div
      data-testid="tdb-month-close-check"
      className={`bg-white rounded-2xl border border-gray-200 p-6 ${className}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-gray-700" />
          <h3 className="text-base font-semibold text-gray-900">Month-Close Gates</h3>
          {closed && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-300 flex items-center gap-1">
              <Lock className="w-3 h-3" /> Closed
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <input
            data-testid="tdb-month-close-picker"
            type="month"
            value={monthYM}
            max={thisMonthYM()}
            onChange={(e) => setMonthYM(e.target.value)}
            className="text-xs px-2 py-1 rounded-md border border-gray-300 bg-white"
          />
          <button
            onClick={load}
            className="text-xs px-2 py-1 rounded-md border border-gray-300 bg-white hover:bg-gray-50"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="space-y-1.5 mb-3" data-testid="tdb-month-close-gates">
        {gates.map((g) => (
          <div
            key={g.id}
            data-testid={`tdb-month-close-gate-${g.id}`}
            className={`flex items-start gap-2 p-2 rounded-md text-xs border ${
              g.passed ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'
            }`}
          >
            {g.passed ? (
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700 mt-0.5 shrink-0" />
            ) : (
              <XCircle className="w-3.5 h-3.5 text-red-700 mt-0.5 shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className={`font-medium ${g.passed ? 'text-emerald-900' : 'text-red-900'}`}>
                <span className="font-mono text-[10px]">{g.id}</span> {g.label}
              </div>
              <div className={`text-[10px] mt-0.5 ${g.passed ? 'text-emerald-700' : 'text-red-700'}`}>
                {g.detail}
              </div>
            </div>
          </div>
        ))}
      </div>

      {err && (
        <div data-testid="tdb-month-close-error" className="mb-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-2">
          {err}
        </div>
      )}

      {!closed && (
        <button
          data-testid="tdb-month-close-approve"
          onClick={approve}
          disabled={!canClose || approving}
          className={`w-full text-sm font-medium px-3 py-2 rounded-md flex items-center justify-center gap-1 ${
            canClose
              ? 'bg-emerald-600 text-white hover:bg-emerald-700'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }`}
          title={canClose ? 'Freeze the month · audit-logged' : 'All gates must be green to approve'}
        >
          <Lock className="w-3.5 h-3.5" />
          {approving ? 'Freezing…' : (canClose ? `Approve & Freeze ${monthYM}` : 'Resolve all gates above to approve')}
        </button>
      )}

      {closed && (
        <div data-testid="tdb-month-close-closed-banner" className="text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded-md p-2">
          Month frozen by <span className="font-medium">{data?.approved_by}</span> at{' '}
          <span className="font-medium">{(data?.approved_at || '').slice(0, 19).replace('T', ' ')}</span>.
        </div>
      )}
    </div>
  );
}
